// validity-field.tsx — DURÉE DE VALIDITÉ d'un titre : nombre + unité + texte imprimé.
//
// ⚠️ PORTAGE FIDÈLE de Sliderz Retail (`apps/sliderz/retail/src/screens/settings-tabs.tsx`, `ValidityField`)
// — décision utilisateur : le wizard doit semer ce que la tablette sait relire, dans SON format. La
// tablette stocke `validity.<titre>` en JSON `{ val, unit, text }` : la valeur brute et l'unité SÉPARÉES,
// jamais converties en jours. Un champ « validité (mois) » ne peut pas exprimer ça — d'où ce composant.
//
// ⚠️ Les helpers ci-dessous MIROITENT ceux de Retail (`unitWord`, `addValidity`). Ils doivent donner le
// MÊME résultat : l'échéance affichée ici est celle que le client lira sur sa caisse. À extraire dans un
// package partagé à la prochaine app qui en aura besoin (règle de 2, §24.3) — pour l'instant la
// duplication est bornée à ces trois fonctions, et signalée.
import { useState, type CSSProperties } from 'react';

/** Unités offertes — mêmes clés qu'en base côté tablette (`j` / `m` / `a`). */
export const UNITS = [{ k: 'j', l: 'jours' }, { k: 'm', l: 'mois' }, { k: 'a', l: 'années' }] as const;

/** Ce que la tablette lit sous `validity.<titre>`. */
export interface TitleValidity { val: number; unit: string; text: string }

/** Accord grammatical — « mois » est invariable, « an/ans » et « jour/jours » non. */
export const unitWord = (v: number, u: string) =>
  (u === 'j' ? (v > 1 ? 'jours' : 'jour') : u === 'm' ? 'mois' : (v > 1 ? 'ans' : 'an'));

/** Échéance depuis une date. ⚠️ Mois et années en ARITHMÉTIQUE DE CALENDRIER (`setMonth`/`setFullYear`),
 *  jamais en multiples de 30 jours : « 3 mois » doit tomber sur le quantième, pas 90 jours plus tard. */
export const addValidity = (from: Date, v: number, u: string): Date => {
  const d = new Date(from.getTime());
  if (u === 'j') d.setDate(d.getDate() + v);
  else if (u === 'm') d.setMonth(d.getMonth() + v);
  else d.setFullYear(d.getFullYear() + v);
  return d;
};

const fmtDate = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

const MIN = 1;
const MAX = 999;

const flecheSt: CSSProperties = {
  width: 26, height: 22, borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'transparent',
  color: 'var(--ink600)', cursor: 'pointer', fontSize: 'var(--t-meta)', lineHeight: 1, display: 'grid', placeItems: 'center',
};

/**
 * Une durée de validité. `makeText` produit la phrase imprimée à partir de (valeur, unité).
 *
 * ⚠️ RÈGLE DE SYNCHRONISATION reprise de Retail : tant que le texte n'a pas été touché à la main, il se
 * RÉGÉNÈRE à chaque changement de durée. Dès qu'on l'édite, il ne bouge plus — sinon on écraserait une
 * formulation choisie par l'exploitant au premier clic sur une flèche.
 */
export function ValidityField({ title, desc, value, onChange, makeText }: {
  title: string;
  desc?: string;
  value: TitleValidity;
  onChange: (v: TitleValidity) => void;
  makeText: (v: number, u: string) => string;
}) {
  const [touched, setTouched] = useState(false);

  const setVal = (v: number) => {
    const borne = Math.min(MAX, Math.max(MIN, v));
    onChange({ val: borne, unit: value.unit, text: touched ? value.text : makeText(borne, value.unit) });
  };
  const setUnit = (u: string) => {
    onChange({ val: value.val, unit: u, text: touched ? value.text : makeText(value.val, u) });
  };

  const echeance = fmtDate(addValidity(new Date(), value.val, value.unit));

  return (
    <div style={{ padding: 14, borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink200)' }}>
      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{title}</div>
      {desc && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 2, lineHeight: 1.5 }}>{desc}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        {/* ⚠️ Valeur NON saisissable au clavier, comme sur la tablette : les flèches garantissent une
            valeur toujours valide (1–999) sans avoir à contrôler une saisie libre. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button type="button" style={{ ...flecheSt, opacity: value.val <= MIN ? 0.35 : 1 }} disabled={value.val <= MIN} onClick={() => setVal(value.val - 1)} aria-label="Diminuer">▾</button>
          <span className="mono" style={{ minWidth: 34, textAlign: 'center', fontSize: 'var(--t-h4)', fontWeight: 700 }}>{value.val}</span>
          <button type="button" style={{ ...flecheSt, opacity: value.val >= MAX ? 0.35 : 1 }} disabled={value.val >= MAX} onClick={() => setVal(value.val + 1)} aria-label="Augmenter">▴</button>
        </div>

        <div style={{ display: 'flex', gap: 2, padding: 3, border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--ink100)' }}>
          {UNITS.map((u) => (
            <button
              key={u.k}
              type="button"
              onClick={() => setUnit(u.k)}
              style={{
                padding: '4px 11px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-dense)', fontWeight: 600,
                background: value.unit === u.k ? 'var(--canvas)' : 'transparent',
                color: value.unit === u.k ? 'var(--ink900)' : 'var(--ink500)',
              }}
            >
              {u.l}
            </button>
          ))}
        </div>
      </div>

      <input
        aria-label={title}
        value={value.text}
        onChange={(e) => { setTouched(true); onChange({ ...value, text: e.target.value }); }}
        style={{ width: '100%', marginTop: 10, padding: '9px 12px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-lead)' }}
      />

      <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap', fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
        <span>Durée réglée aux flèches ; texte pré-rempli modifiable.</span>
        <div style={{ flex: 1 }} />
        {/* L'échéance CALCULÉE, pas promise : c'est la date qu'aurait un titre émis aujourd'hui. */}
        <span className="mono">Émis aujourd’hui → échéance <b style={{ color: 'var(--ink700)' }}>{echeance}</b></span>
      </div>
    </div>
  );
}
